import { useApiQuery } from '../useApiQuery';
import { invoiceKeys } from '@/lib/queryKeys';
import { invoiceEndpoints } from '@/lib/api/endpoints';
import { apiGet, operationsClient } from '@/lib/api/client';
import { normalizeInvoiceStatus } from '@/lib/invoice-status';
import { hasClearingMoneyInFlight } from '@/lib/invoices/shown-invoice-money';
import type { Invoice, InvoiceLineItem } from '@/types/invoice';

function normalizeLineItem(li: Record<string, unknown>): InvoiceLineItem {
  const quantity = Number(li.quantity ?? 1);
  const lineSubtotal =
    li.lineSubtotal != null || li.line_subtotal != null
      ? Number(li.lineSubtotal ?? li.line_subtotal)
      : undefined;
  const unitPrice = Number(
    li.unitPrice ??
      li.unit_price ??
      (lineSubtotal != null && quantity > 0 ? lineSubtotal / quantity : undefined) ??
      li.basePrice ??
      0,
  );
  // NOT-628 — a stored line keeps its discount as an AMOUNT (there is no
  // percent column), so the editor has to read the percent back off it. Without
  // this the line reopened at full price and the next save rebilled the
  // discount the customer had already been given.
  const storedDiscountAmount = Number(li.discountAmount ?? li.discount_amount ?? 0);
  const discountPercent = Number(
    li.discountPercent ??
      li.discount_percent ??
      (storedDiscountAmount > 0 && quantity * unitPrice > 0
        ? (storedDiscountAmount / (quantity * unitPrice)) * 100
        : 0),
  );
  const taxSnapshot = li.taxSnapshot ?? li.tax_snapshot;
  // Generated invoices persist their rates in the snapshot, while legacy
  // invoices expose a percentage directly. Keep explicit legacy values.
  const snapshot =
    taxSnapshot && typeof taxSnapshot === 'object'
      ? (taxSnapshot as Record<string, unknown>)
      : null;
  const breakdown = Array.isArray(snapshot?.breakdown) ? snapshot.breakdown : [];
  const snapshotRate =
    breakdown.length > 0
      ? breakdown.reduce((rate: number, entry: Record<string, unknown>) => {
          const next = Number(entry.rate ?? 0);
          return rate + (entry.isCompound ? (1 + rate) * next : next);
        }, 0)
      : Number(snapshot?.combinedRate ?? 0);
  const taxRate = Number(li.taxRate ?? li.tax_rate ?? snapshotRate * 100);
  const lineTotal = Number(
    li.lineTotal ??
      li.line_total ??
      li.amount ??
      quantity * unitPrice * (1 - discountPercent / 100),
  );

  return {
    id: li.id != null ? String(li.id) : li._id != null ? String(li._id) : undefined,
    lineItemId: li.lineItemId != null ? String(li.lineItemId) : undefined,
    itemVersion: li.itemVersion != null ? Number(li.itemVersion) : undefined,
    code: li.code != null ? String(li.code) : undefined,
    type: li.type != null ? String(li.type) : undefined,
    sourceJobLineKey: typeof li.sourceJobLineKey === 'string' ? li.sourceJobLineKey : undefined,
    paymentScheduleAllocation: li.paymentScheduleAllocation === true,
    unit: li.unit != null ? String(li.unit) : undefined,
    billingSource:
      li.billingSource != null
        ? String(li.billingSource)
        : li.billing_source != null
          ? String(li.billing_source)
          : null,
    basePrice: Number(li.basePrice ?? unitPrice),
    laborRate: li.laborRate != null ? Number(li.laborRate) : undefined,
    costBasis: li.costBasis != null ? Number(li.costBasis) : undefined,
    markupPercent: li.markupPercent != null ? Number(li.markupPercent) : undefined,
    estimatedHours: li.estimatedHours != null ? Number(li.estimatedHours) : undefined,
    actualHours: li.actualHours != null ? Number(li.actualHours) : undefined,
    // NOT-628 — an explicit strategy is what stops `isTMBilledLine` falling
    // through to its heuristic. Dropping it here read a FLAT diagnostic
    // ($89 flat, labour rate 89, no hours) as hourly and printed "0 h x
    // $89.00/h" on the invoice, and the editor then PATCHed the line without
    // a strategy so the stored one could not survive a save either.
    priceStrategy: li.priceStrategy != null ? String(li.priceStrategy) : undefined,
    lineSubtotal,
    // The line's own tax. Documents need it to state what the tax was charged
    // on (Taxable Subtotal); dropping it here left them only the snapshot.
    lineTax: li.lineTax != null ? Number(li.lineTax) : undefined,
    taxable: li.taxable != null ? Boolean(li.taxable) : undefined,
    taxSnapshot:
      taxSnapshot && typeof taxSnapshot === 'object'
        ? (taxSnapshot as InvoiceLineItem['taxSnapshot'])
        : null,
    name: String(li.name ?? li.description ?? 'Item'),
    description:
      li.description !== li.name && li.description != null ? String(li.description) : undefined,
    quantity,
    unitPrice,
    discountPercent,
    membershipDiscountAmount: Number(
      li.membershipDiscountAmount ?? li.membership_discount_amount ?? 0,
    ),
    taxRate,
    lineTotal,
    // What the line CHARGES and whether it may be PRINTED, plus the
    // service ↔ component linkage the customer document groups on. The PDF
    // (`customerVisibleRows(customerFacingLines(...))`) decides from these
    // whether a row prints at all; without them every line printed — an
    // included part at $0.00 and a line the operator had hidden alike — and a
    // rolled-up service itemised. The server stores them on the line's
    // metadata and hydrates them back as plain fields.
    ...carryCustomerDocumentFields(li),
  };
}

/**
 * What a line CHARGES, whether it may be PRINTED, and the service ↔ component
 * linkage the customer document groups on. Both spellings are read, the way
 * the job-line readers (jobLineCosting.ts) and the rest of this normalizer do.
 */
function carryCustomerDocumentFields(
  li: Record<string, unknown>,
): Pick<
  InvoiceLineItem,
  | 'chargeable'
  | 'pricingTreatment'
  | 'visibleToCustomer'
  | 'hiddenFromCustomer'
  | 'showInDetail'
  | 'lineKey'
  | 'parentLineKey'
  | 'componentDisplayMode'
> {
  const bool = (v: unknown) => (typeof v === 'boolean' ? v : undefined);
  const str = (v: unknown) => (v != null ? String(v) : undefined);
  const treatment = li.pricingTreatment ?? li.pricing_treatment;
  const displayMode = li.componentDisplayMode ?? li.component_display_mode;
  return {
    chargeable: bool(li.chargeable),
    pricingTreatment:
      treatment === 'included_in_service' || treatment === 'separate_charge'
        ? treatment
        : undefined,
    visibleToCustomer: bool(li.visibleToCustomer ?? li.visible_to_customer),
    hiddenFromCustomer: bool(li.hiddenFromCustomer ?? li.hidden_from_customer),
    // The THIRD spelling, and the one it is easiest to forget. A pricebook
    // service component saved before the eye existed says 'withheld' with
    // `showInDetail: false`, and BOTH readers still honour it —
    // `visibleToCustomerFlag` here and `isLineHiddenFromCustomer` in
    // operations, the latter with a test named for it. Dropping it printed a
    // line the operator withheld on the operator's own preview while the
    // public page correctly hid it: one document, two answers.
    showInDetail: bool(li.showInDetail ?? li.show_in_detail),
    lineKey: str(li.lineKey ?? li.line_key),
    parentLineKey: str(li.parentLineKey ?? li.parent_line_key),
    componentDisplayMode:
      displayMode === 'ITEMIZED' || displayMode === 'ROLLED_UP' ? displayMode : undefined,
  };
}

function normalizeChecklistReportIds(raw: Record<string, unknown>): string[] {
  const explicitIds = raw.checklistReportIds ?? raw.checklist_report_ids;
  if (Array.isArray(explicitIds)) {
    return [...new Set(explicitIds.filter((id): id is string => typeof id === 'string'))];
  }

  const communicationLog = Array.isArray(raw.communicationLog) ? raw.communicationLog : [];
  for (const entry of [...communicationLog].reverse()) {
    if (!entry || typeof entry !== 'object') continue;
    const record = entry as Record<string, unknown>;
    if (record.success === false) continue;
    const rawIds = record.checklistReportIds ?? record.checklist_report_ids;
    if (!Array.isArray(rawIds)) continue;
    return [...new Set(rawIds.filter((id): id is string => typeof id === 'string'))];
  }

  return [];
}

export function normalizeInvoice(raw: Record<string, unknown>): Invoice {
  const rawContact = raw.contact as Record<string, unknown> | undefined;
  const hasTotal = raw.total != null || raw.grandTotal != null;
  const hasAmountPaid = raw.amountPaid != null;
  const hasBalanceDue = raw.balanceDue != null;
  const contact = rawContact
    ? {
        id: String(rawContact.id ?? ''),
        firstName: String(rawContact.firstName ?? ''),
        lastName: rawContact.lastName != null ? String(rawContact.lastName) : undefined,
        primaryEmail: rawContact.primaryEmail != null ? String(rawContact.primaryEmail) : undefined,
        primaryPhone: rawContact.primaryPhone != null ? String(rawContact.primaryPhone) : undefined,
        company: rawContact.companyName != null ? String(rawContact.companyName) : undefined,
      }
    : undefined;

  return {
    ...raw,
    id: String(raw.id ?? raw._id ?? ''),
    createdBy: typeof raw.createdBy === 'string' ? raw.createdBy : undefined,
    jobId: raw.jobId != null ? String(raw.jobId) : undefined,
    jobType:
      raw.jobType && typeof raw.jobType === 'object'
        ? {
            id: String((raw.jobType as Record<string, unknown>).id ?? ''),
            name: String((raw.jobType as Record<string, unknown>).name ?? ''),
          }
        : null,
    contactId: raw.contactId != null ? String(raw.contactId) : contact?.id,
    generationMode:
      raw.generationMode != null
        ? (String(raw.generationMode) as Invoice['generationMode'])
        : undefined,
    appointmentIds: Array.isArray(raw.appointmentIds)
      ? raw.appointmentIds.map((id) => String(id))
      : undefined,
    milestoneIndex: raw.milestoneIndex != null ? Number(raw.milestoneIndex) : undefined,
    contact,
    lineItems: ((raw.lineItems ?? []) as Record<string, unknown>[]).map(normalizeLineItem),
    subtotal: Number(raw.subtotal ?? 0),
    taxTotal: Number(raw.taxTotal ?? 0),
    discountTotal: Number(raw.discountTotal ?? 0),
    membershipDiscountTotal: Number(raw.membershipDiscountTotal ?? 0),
    total: hasTotal ? Number(raw.total ?? raw.grandTotal) : 0,
    amountPaid: hasAmountPaid ? Number(raw.amountPaid) : 0,
    balanceDue: hasBalanceDue ? Number(raw.balanceDue) : 0,
    financialFieldPresence: {
      total: hasTotal,
      amountPaid: hasAmountPaid,
      balanceDue: hasBalanceDue,
    },
    // The deposit split and the two card-service-fee figures. This mapper is
    // an ALLOWLIST, so #2913's deposit-fee sentence had been unreachable on the
    // office PDF preview since it shipped: the server sent the field and this
    // dropped it on the floor. Absent (a masked role) stays undefined, which
    // the document renders as no sentence at all.
    depositApplied: raw.depositApplied != null ? Number(raw.depositApplied) : undefined,
    depositServiceFeeAmount:
      raw.depositServiceFeeAmount != null ? Number(raw.depositServiceFeeAmount) : undefined,
    paymentServiceFeeAmount:
      raw.paymentServiceFeeAmount != null ? Number(raw.paymentServiceFeeAmount) : undefined,
    tipAmount: Number(raw.tipAmount ?? 0),
    date: String(raw.date ?? raw.issueDate ?? raw.createdAt ?? new Date().toISOString()),
    createdAt: String(raw.createdAt ?? new Date().toISOString()),
    updatedAt: String(raw.updatedAt ?? new Date().toISOString()),
    payments: (raw.payments ?? []) as Invoice['payments'],
    checklistReportIds: normalizeChecklistReportIds(raw),
    status: normalizeInvoiceStatus(raw.status),
    // Wave 2 §3 — lifecycle markers used by InvoiceTimeline + viewedAt
    // chip on the detail panel.
    creditsApplied: raw.creditsApplied != null ? Number(raw.creditsApplied) : undefined,
    viewedAt: typeof raw.viewedAt === 'string' ? raw.viewedAt : null,
    sentAt: typeof raw.sentAt === 'string' ? raw.sentAt : null,
    paidAt: typeof raw.paidAt === 'string' ? raw.paidAt : null,
    wisetackFinancingEnabled: raw.wisetackFinancingEnabled === true,
    wisetack:
      raw.wisetack && typeof raw.wisetack === 'object'
        ? (raw.wisetack as Invoice['wisetack'])
        : null,
  };
}

export function useInvoice(id: string, options?: { enabled?: boolean }) {
  return useApiQuery<Invoice>({
    queryKey: invoiceKeys.detail(id),
    queryFn: async () => {
      const raw = await apiGet<Record<string, unknown>>(
        operationsClient,
        invoiceEndpoints.detail(id),
      );
      return normalizeInvoice(raw);
    },
    enabled: (options?.enabled ?? true) && Boolean(id) && id !== 'new',
    refetchInterval: (query) =>
      query.state.data && hasClearingMoneyInFlight(query.state.data) ? 5_000 : false,
    showErrorToast: false,
  });
}
